import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ForceGraph2D from "react-force-graph-2d";
import {
  Box,
  Typography,
  Button,
  Divider,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ProteinStructureViewer from "./components/ProteinStructureViewer";
import { getChainColor } from "./utils/chainColors";
import {
  INTERACTION_TYPE_ORDER,
  INTERACTION_TYPE_COLORS,
  INTERACTION_TYPE_LABELS,
  UNCLASSIFIED_EDGE_COLOR,
  getInteractionColor
} from "./utils/interactionColors";

const unique = values => Array.from(new Set((values || []).filter(Boolean)));
const MemoForceGraph2D = React.memo(ForceGraph2D);

const deriveLigandMapping = rowData => {
  if (!rowData?.highlight?.node_mapping || !rowData?.ligand_graph?.nodes) {
    return {
      highlightLigandNodes: new Set(),
      nonMappingLigandChains: [],
      validLigandChains: []
    };
  }

  const selectedTargetChain = rowData.Target_Chain;
  const nodeMappings = rowData.highlight.node_mapping;

  const validLigandChains = [
    ...new Set(
      nodeMappings
        .filter(mapping => mapping.targ_chain === selectedTargetChain)
        .map(mapping => mapping.lig_chain)
    )
  ];

  const allLigandChains = new Set(
    rowData.ligand_graph.nodes.map(node => node.chain)
  );

  const nonMappingLigandChains = [...allLigandChains].filter(
    chain => !validLigandChains.includes(chain)
  );

  const highlightLigandNodes = new Set(
    nodeMappings
      .filter(
        mapping =>
          mapping.targ_chain === selectedTargetChain &&
          validLigandChains.includes(mapping.lig_chain)
      )
      .map(mapping => String(mapping.lig_node))
  );

  return {
    highlightLigandNodes,
    nonMappingLigandChains,
    validLigandChains
  };
};

const collectResidues = (nodes, predicate) =>
  unique(
    (Array.isArray(nodes) ? nodes : [])
      .filter(predicate)
      .map(node => node.residue_number)
  ).sort((a, b) => Number(a) - Number(b));

const buildNodeLabel = node =>
  `
    <div style="background-color:#1E293B;padding:6px;border-radius:6px;color:#E5E7EB;font-size:12px">
      <b>Residue:</b> ${node.residue_name || "N/A"} ${node.residue_number || "?"}<br/>
      <b>Chain:</b> ${node.chain || "N/A"}<br/>
      <b>Atom:</b> ${node.atom_name || "N/A"}<br/>
      <b>Structure:</b> ${node.structureKind === "query" ? "Graph Query" : "Graph Pattern Match"}
    </div>
  `;

const buildQueryGraphData = rowData => {
  if (!rowData?.target_graph) return { nodes: [], links: [] };

  return {
    nodes: rowData.target_graph.nodes.map(node => ({
      ...node,
      id: `Q-${node.id}`,
      sourceNodeId: String(node.id),
      structureKind: "query",
      isFocus: node.chain === rowData?.Target_Chain,
      label: buildNodeLabel({ ...node, structureKind: "query" })
    })),
    links: rowData.target_graph.links.map((link, index) => ({
      ...link,
      source: `Q-${link.source}`,
      target: `Q-${link.target}`,
      label: link.interaction_type?.join(" | ") || `Query edge ${index + 1}`
    }))
  };
};

const buildPatternGraphData = (rowData, highlightLigandNodes, validLigandChains) => {
  if (!rowData?.ligand_graph) return { nodes: [], links: [] };

  return {
    nodes: rowData.ligand_graph.nodes.map(node => ({
      ...node,
      id: `P-${node.id}`,
      sourceNodeId: String(node.id),
      structureKind: "pattern",
      isMapped: highlightLigandNodes.has(String(node.id)),
      isChainMatch: validLigandChains.includes(node.chain),
      label: buildNodeLabel({ ...node, structureKind: "pattern" })
    })),
    links: rowData.ligand_graph.links.map((link, index) => ({
      ...link,
      source: `P-${link.source}`,
      target: `P-${link.target}`,
      label: link.interaction_type?.join(" | ") || `Pattern edge ${index + 1}`
    }))
  };
};

const buildMappingGraphData = (rowData, highlightLigandNodes, validLigandChains) => {
  if (!rowData?.target_graph || !rowData?.ligand_graph) {
    return { nodes: [], links: [] };
  }

  const targetNodes = rowData.target_graph.nodes
    .filter(node => node.chain === rowData.Target_Chain)
    .map(node => ({
      ...node,
      id: `M-Q-${node.id}`,
      sourceNodeId: String(node.id),
      structureKind: "query",
      isFocus: true,
      label: buildNodeLabel({ ...node, structureKind: "query" })
    }));

  const ligandNodes = rowData.ligand_graph.nodes
    .filter(node =>
      highlightLigandNodes.has(String(node.id)) ||
      validLigandChains.includes(node.chain)
    )
    .map(node => ({
      ...node,
      id: `M-P-${node.id}`,
      sourceNodeId: String(node.id),
      structureKind: "pattern",
      label: buildNodeLabel({ ...node, structureKind: "pattern" })
    }));

  const targetNodeIds = new Set(targetNodes.map(node => node.sourceNodeId));
  const ligandNodeIds = new Set(ligandNodes.map(node => node.sourceNodeId));

  const targetLinks = rowData.target_graph.links
    .filter(link => targetNodeIds.has(String(link.source)) && targetNodeIds.has(String(link.target)))
    .map(link => ({
      ...link,
      source: `M-Q-${link.source}`,
      target: `M-Q-${link.target}`,
      label: link.interaction_type?.join(" | ") || "Target mapping edge",
      edgeKind: "query"
    }));

  const ligandLinks = rowData.ligand_graph.links
    .filter(link => ligandNodeIds.has(String(link.source)) && ligandNodeIds.has(String(link.target)))
    .map(link => ({
      ...link,
      source: `M-P-${link.source}`,
      target: `M-P-${link.target}`,
      label: link.interaction_type?.join(" | ") || "Pattern mapping edge",
      edgeKind: "pattern"
    }));

  const mappingLinks = (rowData.highlight?.node_mapping || []).map(mapping => ({
    source: `M-Q-${mapping.targ_node}`,
    target: `M-P-${mapping.lig_node}`,
    label: `${mapping.targ_chain} -> ${mapping.lig_chain}`,
    edgeKind: "mapping",
    dashed: true
  }));

  return {
    nodes: [...targetNodes, ...ligandNodes],
    links: [...targetLinks, ...ligandLinks, ...mappingLinks]
  };
};

export default function GraphExplorer() {
  const location = useLocation();
  const navigate = useNavigate();
  const rowData = location.state?.rowData || null;
  const returnTo = location.state?.returnTo || -1;
  const returnState = location.state?.returnState;

  const graphContainerRef = useRef(null);
  const graphRef = useRef(null);
  const hoverFrameRef = useRef(null);
  const lastHoveredNodeIdRef = useRef("");
  const hasFrozenLayoutRef = useRef(false);
  const [graphSize, setGraphSize] = useState({ width: 900, height: 380 });
  const [selectedStructure, setSelectedStructure] = useState("query");
  const [selectedPatternId, setSelectedPatternId] = useState("pattern-target");
  const [hoveredNodeId, setHoveredNodeId] = useState("");
  const [selectedNode, setSelectedNode] = useState(null);
  const [visibleRepresentations, setVisibleRepresentations] = useState({
    cartoon: true,
    surface: false,
    ballStick: true,
    chains: true
  });
  const [visibleChainsByStructure, setVisibleChainsByStructure] = useState({
    query: [],
    pattern: []
  });

  useEffect(() => {
    if (!graphContainerRef.current) {
      return undefined;
    }

    const updateSize = () => {
      if (!graphContainerRef.current) return;

      const { width } = graphContainerRef.current.getBoundingClientRect();
      setGraphSize({
        width: Math.max(320, Math.floor(width) - 16),
        height: width < 700 ? 320 : 380
      });
    };

    updateSize();

    const observer = new ResizeObserver(updateSize);
    observer.observe(graphContainerRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => () => {
    if (hoverFrameRef.current) {
      window.cancelAnimationFrame(hoverFrameRef.current);
    }
  }, []);

  const handleBack = () => {
    if (typeof returnTo === "string") {
      navigate(returnTo, { state: returnState });
      return;
    }

    navigate(returnTo);
  };

  const {
    highlightLigandNodes,
    nonMappingLigandChains,
    validLigandChains
  } = useMemo(() => deriveLigandMapping(rowData), [rowData]);

  const queryResidues = useMemo(
    () => collectResidues(
      rowData?.target_graph?.nodes,
      node => node.chain === rowData?.Target_Chain
    ),
    [rowData]
  );

  const patternResidues = useMemo(
    () => collectResidues(
      rowData?.ligand_graph?.nodes,
      node =>
        highlightLigandNodes.has(String(node.id)) ||
        validLigandChains.includes(node.chain)
    ),
    [highlightLigandNodes, rowData, validLigandChains]
  );

  const patternItems = useMemo(() => {
    if (!rowData?.highlight) return [];

    const queryNodeCount = rowData?.target_graph?.nodes?.filter(node => node.chain === rowData?.Target_Chain).length || 0;
    const patternNodeCount = rowData?.ligand_graph?.nodes?.filter(
      node => highlightLigandNodes.has(String(node.id)) || validLigandChains.includes(node.chain)
    ).length || 0;
    const mappingCount = rowData.highlight?.node_mapping?.length || 0;

    return [
      {
        id: "pattern-target",
        label: "Graph Query Interactions",
        secondary: `${rowData.highlight?.target_edges?.length || 0} edges`,
        detail: `${queryNodeCount} residues in focus`,
        chainSummary: unique([rowData?.Target_Chain]).join(", ") || "N/A",
        structureKind: "query"
      },
      {
        id: "pattern-ligand",
        label: "Graph Pattern Match",
        secondary: `${rowData.highlight?.ligand_edges?.length || 0} edges`,
        detail: `${patternNodeCount} residues in focus`,
        chainSummary: validLigandChains.join(", ") || unique(rowData?.Ligand_Chains).join(", ") || "N/A",
        structureKind: "pattern"
      },
      {
        id: "pattern-mapping",
        label: "Residue Mapping Bridge",
        secondary: `${mappingCount} mapped nodes`,
        detail: `${rowData.highlight?.target_edges?.length || 0} query edges + ${rowData.highlight?.ligand_edges?.length || 0} pattern edges`,
        chainSummary: `${rowData?.Target_Chain || "?"} -> ${validLigandChains.join(", ") || "N/A"}`,
        structureKind: "pattern"
      }
    ];
  }, [highlightLigandNodes, rowData, validLigandChains]);

  const queryGraphData = useMemo(
    () => buildQueryGraphData(rowData),
    [rowData]
  );

  const patternGraphData = useMemo(
    () => buildPatternGraphData(rowData, highlightLigandNodes, validLigandChains),
    [highlightLigandNodes, rowData, validLigandChains]
  );

  const mappingGraphData = useMemo(
    () => buildMappingGraphData(rowData, highlightLigandNodes, validLigandChains),
    [highlightLigandNodes, rowData, validLigandChains]
  );

  const activeGraphData = useMemo(() => {
    if (selectedPatternId === "pattern-target") return queryGraphData;
    if (selectedPatternId === "pattern-ligand") return patternGraphData;
    return mappingGraphData;
  }, [mappingGraphData, patternGraphData, queryGraphData, selectedPatternId]);

  const availableChainsByStructure = useMemo(() => ({
    query: unique(
      rowData?.target_graph?.nodes
        .map(node => node.chain)
    ),
    pattern: unique(
      rowData?.ligand_graph?.nodes
        ?.filter(node => validLigandChains.includes(node.chain) || (rowData?.Ligand_Chains || []).includes(node.chain))
        .map(node => node.chain)
    )
  }), [rowData, validLigandChains]);

  useEffect(() => {
    setVisibleChainsByStructure(prev => ({
      query: prev.query.length ? prev.query.filter(chain => availableChainsByStructure.query.includes(chain)) : availableChainsByStructure.query,
      pattern: prev.pattern.length ? prev.pattern.filter(chain => availableChainsByStructure.pattern.includes(chain)) : availableChainsByStructure.pattern
    }));
  }, [availableChainsByStructure]);

  useEffect(() => {
    hasFrozenLayoutRef.current = false;
  }, [activeGraphData]);

  const selectedViewer = useMemo(() => {
    const ligandHighlightChains = unique(
      nonMappingLigandChains.length === 0
        ? validLigandChains
        : [...validLigandChains, ...nonMappingLigandChains, ...(rowData?.Ligand_Chains || [])]
    );

    const viewers = {
      query: {
        id: "query",
        label: "Graph Query",
        pdbId: rowData?.PDBID_Targ,
        subtitle: `Showing all query chains. Target chain: ${rowData?.Target_Chain || "N/A"}`,
        highlightChains: [],
        baseResidues: queryResidues
      },
      pattern: {
        id: "pattern",
        label: "Graph Pattern Match",
        pdbId: rowData?.PDBID_Lig,
        subtitle: `Predicted Chain(s) ${ligandHighlightChains.join(", ") || "N/A"}`,
        highlightChains: ligandHighlightChains,
        baseResidues: patternResidues
      }
    };

    return viewers[selectedStructure] || viewers.query;
  }, [nonMappingLigandChains, patternResidues, queryResidues, rowData, selectedStructure, validLigandChains]);

  const activeVisibleChains = useMemo(() => {
    const configured = visibleChainsByStructure[selectedStructure] || [];
    const available = availableChainsByStructure[selectedStructure] || [];
    return configured.length ? configured : available;
  }, [availableChainsByStructure, selectedStructure, visibleChainsByStructure]);

  const hoveredNode = useMemo(
    () => activeGraphData.nodes.find(node => node.id === hoveredNodeId) || null,
    [activeGraphData.nodes, hoveredNodeId]
  );

  const viewerBaseResidues = useMemo(
    () => unique(selectedViewer.baseResidues),
    [selectedViewer.baseResidues]
  );

  const viewerSelectedResidues = useMemo(
    () => unique(
      selectedNode?.structureKind === selectedStructure && selectedNode?.residue_number
        ? [selectedNode.residue_number]
        : []
    ),
    [selectedNode, selectedStructure]
  );

  const viewerHoveredResidues = useMemo(
    () => unique(
      hoveredNode?.structureKind === selectedStructure && hoveredNode?.residue_number
        ? [hoveredNode.residue_number]
        : []
    ),
    [hoveredNode, selectedStructure]
  );

  const viewerHighlightChains = useMemo(() => {
    if (selectedStructure === "query") {
      return [];
    }

    const chains = [...(selectedViewer.highlightChains || [])];

    if (selectedNode?.structureKind === selectedStructure && selectedNode?.chain) {
      chains.push(selectedNode.chain);
    }

    if (hoveredNode?.structureKind === selectedStructure && hoveredNode?.chain) {
      chains.push(hoveredNode.chain);
    }

    return unique(chains);
  }, [hoveredNode, selectedNode, selectedStructure, selectedViewer.highlightChains]);

  const summaryItems = useMemo(() => {
    if (!rowData) return [];

    return [
      {
        label: "Graph Query Chains",
        value: unique(rowData.Target_Chains || rowData.All_Target_Chains || [rowData.Target_Chain]).join(", ") || "N/A"
      },
      {
        label: "Pattern Ligand Chains",
        value: unique(rowData.Ligand_Chains || rowData.predicted_ligand_chains).join(", ") || "N/A"
      },
      {
        label: "Highlighted Query Residues",
        value: queryResidues.join(", ") || "N/A"
      },
      {
        label: "Highlighted Pattern Residues",
        value: patternResidues.join(", ") || "N/A"
      },
      {
        label: "Confidence",
        value: rowData.pick_score != null ? Number(rowData.pick_score).toFixed(3) : "N/A"
      },
      {
        label: "Graph Nodes Visible",
        value: String(activeGraphData.nodes.length)
      }
    ];
  }, [activeGraphData.nodes.length, patternResidues, queryResidues, rowData]);

  // Pocket and Electrostatic were removed from this list (not just
  // disabled) -- both need real backend work this pipeline doesn't do yet
  // (cavity detection, precomputed partial charges), and a chip that looks
  // clickable but silently does nothing reads as broken, not "coming soon".
  const representationItems = [
    { key: "cartoon", label: "Cartoon" },
    { key: "surface", label: "Surface" },
    { key: "ballStick", label: "Ball & Stick" },
    { key: "chains", label: "Chains" }
  ];

  const activePatternLabel = patternItems.find(item => item.id === selectedPatternId)?.label || "Interaction Graph";
  const activePatternMeta = patternItems.find(item => item.id === selectedPatternId) || null;
  // "Residue Mapping Bridge" spans both structures, so its focus is the
  // union of query + pattern residues rather than either list alone.
  const focusedResidues = selectedPatternId === "pattern-mapping"
    ? unique([...queryResidues, ...patternResidues]).sort((a, b) => Number(a) - Number(b))
    : activePatternMeta?.structureKind === "query" ? queryResidues : patternResidues;
  // This page always shows exactly one match, opened from one specific
  // results row -- a 0-1 filter slider can only ever be fully-on or
  // fully-off for that one fixed score, which isn't really "filtering" in
  // any useful sense. A persistent, color-coded badge communicates the same
  // information without ever hiding content the user explicitly navigated to.
  const confidenceScore = Number(rowData?.pick_score ?? 0);
  const confidenceTone = confidenceScore >= 0.7
    ? { bg: "#dcfce7", fg: "#15803d", label: "High" }
    : confidenceScore >= 0.4
      ? { bg: "#fef9c3", fg: "#92400e", label: "Medium" }
      : { bg: "#fee2e2", fg: "#b91c1c", label: "Low" };

  const toggleRepresentation = key => {
    if (!(key in visibleRepresentations)) return;

    setVisibleRepresentations(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handlePatternSelect = patternId => {
    setSelectedPatternId(patternId);
    setHoveredNodeId("");
    setSelectedNode(null);

    const nextPattern = patternItems.find(item => item.id === patternId);
    if (nextPattern?.structureKind) {
      setSelectedStructure(nextPattern.structureKind);
    }
  };

  const handleStructureFocus = structureKind => {
    setSelectedStructure(structureKind);
    if (structureKind === "query") {
      setSelectedPatternId("pattern-target");
    } else if (structureKind === "pattern") {
      setSelectedPatternId("pattern-ligand");
    }
  };

  const handleChainToggle = chain => {
    setVisibleChainsByStructure(prev => {
      const current = prev[selectedStructure] || [];
      const exists = current.includes(chain);
      const next = exists
        ? current.filter(item => item !== chain)
        : [...current, chain];

      return {
        ...prev,
        [selectedStructure]: next
      };
    });
  };

  const resetVisibleChains = () => {
    setVisibleChainsByStructure(prev => ({
      ...prev,
      [selectedStructure]: availableChainsByStructure[selectedStructure] || []
    }));
  };

  const filteredGraphData = useMemo(() => {
    if (!activeGraphData.nodes.length) {
      return activeGraphData;
    }

    if (selectedPatternId === "pattern-target") {
      const allowed = new Set(activeVisibleChains);
      const nodes = activeGraphData.nodes.filter(node => allowed.has(node.chain));
      const ids = new Set(nodes.map(node => node.id));
      const links = activeGraphData.links.filter(link => ids.has(link.source.id || link.source) && ids.has(link.target.id || link.target));
      return { nodes, links };
    }

    if (selectedPatternId === "pattern-ligand") {
      const allowed = new Set(activeVisibleChains);
      const nodes = activeGraphData.nodes.filter(node => allowed.has(node.chain));
      const ids = new Set(nodes.map(node => node.id));
      const links = activeGraphData.links.filter(link => ids.has(link.source.id || link.source) && ids.has(link.target.id || link.target));
      return { nodes, links };
    }

    const allowedQuery = new Set(visibleChainsByStructure.query.length ? visibleChainsByStructure.query : availableChainsByStructure.query);
    const allowedPattern = new Set(visibleChainsByStructure.pattern.length ? visibleChainsByStructure.pattern : availableChainsByStructure.pattern);
    const nodes = activeGraphData.nodes.filter(node =>
      node.structureKind === "query" ? allowedQuery.has(node.chain) : allowedPattern.has(node.chain)
    );
    const ids = new Set(nodes.map(node => node.id));
    const links = activeGraphData.links.filter(link => ids.has(link.source.id || link.source) && ids.has(link.target.id || link.target));
    return { nodes, links };
  }, [activeGraphData, activeVisibleChains, availableChainsByStructure, selectedPatternId, visibleChainsByStructure]);

  // Some nodes (most often in "Residue Mapping Bridge": ligand residues
  // pulled in for same-chain context rather than a specific mapping) end up
  // with no edge at all in the currently rendered graph. Left at full size
  // and full chain color they're indistinguishable from genuinely connected
  // residues, reading as noise or a rendering bug rather than intentional
  // context -- drawNode demotes them instead of dropping them.
  const connectedNodeIds = useMemo(() => {
    const ids = new Set();
    for (const link of filteredGraphData.links) {
      const sourceId = link.source?.id ?? link.source;
      const targetId = link.target?.id ?? link.target;
      if (sourceId != null) ids.add(sourceId);
      if (targetId != null) ids.add(targetId);
    }
    return ids;
  }, [filteredGraphData]);
  const hasOrphanNodes = filteredGraphData.nodes.some(node => !connectedNodeIds.has(node.id));
  const presentInteractionTypes = useMemo(
    () => INTERACTION_TYPE_ORDER.filter(type =>
      filteredGraphData.links.some(link => link.interaction_type?.includes(type))
    ),
    [filteredGraphData]
  );

  const handleNodeClick = node => {
    if (!node) return;

    setSelectedNode(prev => (prev?.id === node.id ? null : node));
    if (node.structureKind === "query" || node.structureKind === "pattern") {
      setSelectedStructure(node.structureKind);
    }
  };

  const drawNode = useCallback((node, ctx) => {
    const isHovered = hoveredNodeId === node.id;
    const isSelected = selectedNode?.id === node.id;
    const isFocus = node.isMapped || node.isFocus;
    const isOrphan = !connectedNodeIds.has(node.id);
    const radius = isSelected ? 7.2 : isHovered ? 5.8 : isFocus ? 5.1 : isOrphan ? 3 : 4.1;
    const fillStyle = isOrphan && !isSelected && !isHovered ? "#c7ccd6" : getChainColor(node.chain);

    ctx.beginPath();
    ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI);
    ctx.fillStyle = fillStyle;
    ctx.fill();

    if (isFocus || isSelected || isHovered) {
      ctx.lineWidth = isSelected ? 2.4 : isHovered ? 2.2 : 1.8;
      ctx.strokeStyle = isSelected ? "#dc2626" : isHovered ? "#f59e0b" : "#facc15";
      ctx.stroke();
    }
  }, [connectedNodeIds, hoveredNodeId, selectedNode]);

  const drawLink = useCallback((link, ctx) => {
    const isMapping = link.edgeKind === "mapping" || link.dashed;
    const typeCount = Array.isArray(link.interaction_type) ? link.interaction_type.length : 0;
    ctx.save();

    if (isMapping) {
      ctx.strokeStyle = "#ef4444";
      ctx.lineWidth = 1.4;
      ctx.setLineDash([4, 3]);
    } else {
      // Interaction chemistry is the single most scientifically meaningful
      // signal this tool produces -- encode it as the edge color instead of
      // leaving it as hover-only tooltip text. An edge with several
      // simultaneous interaction types (the backend bitmask-combines them)
      // renders a touch thicker so "more chemistry here" is visible at a
      // glance, not just in the label.
      ctx.strokeStyle = getInteractionColor(link.interaction_type);
      ctx.lineWidth = typeCount > 1 ? 1.6 : 0.9;
    }

    ctx.beginPath();
    ctx.moveTo(link.source.x, link.source.y);
    ctx.lineTo(link.target.x, link.target.y);
    ctx.stroke();
    ctx.restore();
  }, []);

  const handleGraphEngineStop = useCallback(() => {
    if (!graphRef.current) {
      return;
    }

    // Small/sparse graphs (the median case here -- 3-node query interfaces
    // are the single most common size in real prediction jobs) settle in
    // the middle of the canvas at whatever zoom the simulation happened to
    // start at, leaving them looking lost or still-loading in a mostly
    // empty box. Fit the camera to the actual layout on every settle, not
    // just once ever -- otherwise switching patterns while the previous
    // graph's "already frozen" flag is still set (a real timing race with
    // the effect below) leaves the camera stuck at the old graph's zoom
    // level, which can crop the new graph almost entirely out of view.
    // Duration 0 (instant) since the physics has already settled by the
    // time onEngineStop fires -- no need to animate the camera too.
    graphRef.current.zoomToFit(0, 40);

    if (hasFrozenLayoutRef.current) {
      return;
    }

    hasFrozenLayoutRef.current = true;
    // Give the canvas one frame to actually repaint at the new zoom/pan
    // before freezing the render loop -- pausing in the same tick as
    // zoomToFit can stop the loop before that repaint happens, leaving the
    // canvas visually stuck on the frame from just before the fit was
    // applied even though the internal camera state is already correct.
    window.requestAnimationFrame(() => {
      if (!graphRef.current) return;
      graphRef.current.pauseAnimation();
      window.requestAnimationFrame(() => {
        if (graphRef.current) {
          graphRef.current.resumeAnimation();
        }
      });
    });
  }, []);

  const handleNodeHover = useCallback(node => {
    const nextId = node?.id || "";
    if (nextId === lastHoveredNodeIdRef.current) {
      return;
    }

    lastHoveredNodeIdRef.current = nextId;
    if (hoverFrameRef.current) {
      window.cancelAnimationFrame(hoverFrameRef.current);
    }

    hoverFrameRef.current = window.requestAnimationFrame(() => {
      setHoveredNodeId(nextId);
    });
  }, []);

  const handleNodePointerAreaPaint = useCallback((node, color, ctx) => {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(node.x, node.y, 7, 0, 2 * Math.PI);
    ctx.fill();
  }, []);

  const accordionSx = {
    "&:before": { display: "none" },
    border: "1px solid #e2e8f0",
    borderRadius: "12px !important",
    mb: 1.5,
    "&:last-of-type": { mb: 0 }
  };

  const chainChips = availableChainsByStructure[selectedStructure] || [];
  const residueFocusNode = selectedNode || hoveredNode || null;
  const chainLegendItems = useMemo(
    () => unique([
      ...availableChainsByStructure.query,
      ...availableChainsByStructure.pattern
    ]),
    [availableChainsByStructure]
  );
  const isLargeGraph = filteredGraphData.nodes.length > 140 || filteredGraphData.links.length > 220;
  const graphEmptyByChainFilter = activeGraphData.nodes.length > 0 && filteredGraphData.nodes.length === 0;
  const graphEmptyWithoutData = activeGraphData.nodes.length === 0;

  if (!rowData) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>No explorer data available</Typography>
        <Typography sx={{ mb: 2 }}>
          Open this page from the results table using the `Explore` button.
        </Typography>
        <Button variant="outlined" onClick={handleBack}>
          Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: { xs: "1.5rem 4%", md: "8rem 10%" }, minHeight: "100vh", background: "linear-gradient(180deg, #f7f9fc 0%, #edf3f8 100%)" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Graph Explorer
          </Typography>
          <Typography sx={{ color: "#526173", mt: 1, maxWidth: 860 }}>
            The explorer now links structure, graph, residue details, and chain-aware controls in one workspace so Graph Query and Graph Pattern Match can be compared more fluidly.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {/* <Button variant="outlined" onClick={() => navigate("/graph", { state: location.state })}>
            Open Classic Graph View
          </Button> */}
          <Button variant="contained" onClick={handleBack}>
            Back To Results
          </Button>
        </Box>
      </Box>

      {/* `1fr` tracks default to minmax(auto, 1fr) -- they refuse to shrink
          below their content's min-content width. The 2D/3D graph canvases
          measure their own container to size themselves, so without
          minmax(0, 1fr) here that becomes a feedback loop: the canvas's
          initial default width holds the column open, which holds the
          canvas's measured width up, and the whole grid never shrinks below
          ~900px even on a narrow phone. */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "320px minmax(0, 1fr)" }, gap: 3 }}>
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid #dbe4ec", backgroundColor: "#fff", minWidth: 0 }}>
          <Typography variant="overline" sx={{ color: "#355070", fontWeight: 700 }}>
            Control Rail
          </Typography>
          <Typography variant="h6" sx={{ mt: 1, mb: 2 }}>
            Explorer Controls
          </Typography>

          {/* Grouped by how often each section is touched during a session
              (Findings 11): Display controls change constantly, Patterns
              change per-selection, Reference is consulted occasionally and
              starts collapsed so it doesn't push the working controls below
              the fold. */}
          <Accordion defaultExpanded disableGutters elevation={0} sx={accordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Display</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Representation
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                {representationItems.map(item => (
                  <Chip
                    key={item.key}
                    label={item.label}
                    clickable
                    color={visibleRepresentations[item.key] ? "primary" : "default"}
                    variant={visibleRepresentations[item.key] ? "filled" : "outlined"}
                    onClick={() => toggleRepresentation(item.key)}
                  />
                ))}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Structure Focus
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Chip
                  label="Graph Query"
                  color={selectedStructure === "query" ? "primary" : "default"}
                  variant={selectedStructure === "query" ? "filled" : "outlined"}
                  onClick={() => handleStructureFocus("query")}
                />
                <Chip
                  label="Graph Pattern Match"
                  color={selectedStructure === "pattern" ? "primary" : "default"}
                  variant={selectedStructure === "pattern" ? "filled" : "outlined"}
                  onClick={() => handleStructureFocus("pattern")}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Chain Visibility
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {chainChips.map(chain => (
                  <Chip
                    key={`${selectedStructure}-${chain}`}
                    label={chain}
                    onClick={() => handleChainToggle(chain)}
                    variant={activeVisibleChains.includes(chain) ? "filled" : "outlined"}
                    sx={{
                      backgroundColor: activeVisibleChains.includes(chain) ? getChainColor(chain) : undefined,
                      color: activeVisibleChains.includes(chain) ? "#fff" : undefined
                    }}
                  />
                ))}
              </Stack>
              <Button size="small" sx={{ mt: 1 }} onClick={resetVisibleChains}>
                Reset Chains
              </Button>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Confidence
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  backgroundColor: confidenceTone.bg,
                  border: "1px solid #d8e0e8"
                }}
              >
                <Typography variant="body2" sx={{ color: confidenceTone.fg, fontWeight: 700 }}>
                  {confidenceScore.toFixed(3)}
                </Typography>
                <Chip
                  size="small"
                  label={confidenceTone.label}
                  sx={{ backgroundColor: confidenceTone.fg, color: "#fff", fontWeight: 600 }}
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded disableGutters elevation={0} sx={accordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Patterns</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Pattern List
              </Typography>
              <List dense disablePadding>
                {patternItems.map(item => (
                  <ListItemButton
                    key={item.id}
                    selected={selectedPatternId === item.id}
                    onClick={() => handlePatternSelect(item.id)}
                    sx={{ borderRadius: 2, mb: 0.5 }}
                  >
                    <ListItemText
                      primary={item.label}
                      secondary={`${item.secondary} | ${item.detail}`}
                    />
                  </ListItemButton>
                ))}
              </List>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Residues In Focus
              </Typography>
              <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: "#f7f9fc", border: "1px solid #d8e0e8", mb: 2 }}>
                {focusedResidues.length > 0 ? (
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                    {focusedResidues.map(residue => (
                      <Chip key={residue} size="small" variant="outlined" label={residue} />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: "#607086" }}>
                    No residues in focus for {activePatternMeta?.label || "this pattern"}.
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Residue Detail
              </Typography>
              <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: "#f7f9fc", border: "1px solid #d8e0e8" }}>
                {residueFocusNode ? (
                  <Box sx={{ display: "grid", gap: 0.75 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {selectedNode ? "Locked Selection" : "Hover Preview"}
                    </Typography>
                    <Typography variant="body2">
                      {residueFocusNode.residue_name || "Residue"} {residueFocusNode.residue_number || "?"} on chain {residueFocusNode.chain || "?"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#607086" }}>
                      {residueFocusNode.structureKind === "query" ? "Graph Query" : "Graph Pattern Match"} node
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#607086" }}>
                      Atom: {residueFocusNode.atom_name || "N/A"}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: "#607086" }}>
                    Hover a node to preview the corresponding residue, or click one to keep it selected across interactions.
                  </Typography>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion disableGutters elevation={0} sx={accordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Reference</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Legend
              </Typography>
              <Box sx={{ display: "grid", gap: 1.1 }}>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {chainLegendItems.map(chain => (
                    <Chip
                      key={`legend-${chain}`}
                      size="small"
                      label={`Chain ${chain}`}
                      sx={{
                        backgroundColor: getChainColor(chain),
                        color: "#fff",
                        fontWeight: 600
                      }}
                    />
                  ))}
                </Box>
                {presentInteractionTypes.length > 0 && (
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {presentInteractionTypes.map(type => (
                      <Chip
                        key={`legend-interaction-${type}`}
                        size="small"
                        variant="outlined"
                        label={INTERACTION_TYPE_LABELS[type]}
                        sx={{
                          borderColor: INTERACTION_TYPE_COLORS[type],
                          color: INTERACTION_TYPE_COLORS[type],
                          fontWeight: 600
                        }}
                      />
                    ))}
                    <Chip
                      size="small"
                      variant="outlined"
                      label="Unclassified contact"
                      sx={{ borderColor: UNCLASSIFIED_EDGE_COLOR, color: "#607086", fontWeight: 600 }}
                    />
                  </Box>
                )}
                <Typography variant="body2" sx={{ color: "#607086" }}>
                  Red outline marks selected nodes. Amber outline marks mapped pattern residues. Dashed red links show residue mapping bridges{presentInteractionTypes.length > 0 ? "; other link colors show interaction chemistry (hover an edge for details)" : ""}.
                </Typography>
                {hasOrphanNodes && (
                  <Typography variant="body2" sx={{ color: "#607086" }}>
                    Small grey dots are context residues on a matched chain that aren't part of a specific mapping in this view.
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Match Summary
              </Typography>
              <Box sx={{ display: "grid", gap: 1.25 }}>
                {summaryItems.map(item => (
                  <Box key={item.label}>
                    <Typography variant="caption" sx={{ color: "#6b7280", textTransform: "uppercase" }}>
                      {item.label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Paper>

        <Box sx={{ display: "grid", gap: 3, alignContent: "start", alignItems: "start", minWidth: 0 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid #dbe4ec", backgroundColor: "#fff", minWidth: 0 }}>
            <Typography variant="overline" sx={{ color: "#355070", fontWeight: 700 }}>
              Structure Workspace
            </Typography>
            <Typography variant="h6" sx={{ mt: 1 }}>
              {selectedViewer.label}: {selectedViewer.pdbId}
            </Typography>
            <Typography sx={{ color: "#607086", mb: 2 }}>
              {selectedViewer.subtitle}
            </Typography>

            <ProteinStructureViewer
              key={`${selectedViewer.id}-${selectedViewer.pdbId}`}
              pdbId={selectedViewer.pdbId}
              highlightChains={viewerHighlightChains}
              visibleChains={activeVisibleChains}
              baseHighlightResidues={viewerBaseResidues}
              hoveredResidues={viewerHoveredResidues}
              selectedResidues={viewerSelectedResidues}
              visibleRepresentations={visibleRepresentations}
              minHeight={460}
            />

            <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center",  height: 40 }}>
              {hoveredNode && (
                <Chip
                  label={`Hovering: ${hoveredNode.residue_name || "Residue"} ${hoveredNode.residue_number || ""} (${hoveredNode.chain || "?"})`}
                  variant="outlined"
                />
              )}
              {selectedNode && (
                <Chip
                  label={`Selected: ${selectedNode.residue_name || "Residue"} ${selectedNode.residue_number || ""} (${selectedNode.chain || "?"})`}
                  color="primary"
                  variant="outlined"
                />
              )}
              {!hoveredNode && !selectedNode && (
                <Typography variant="body2" sx={{ color: "#7b8794" }}>
                  Hover or select a node to see residue status here.
                </Typography>
              )}
            </Box>
          </Paper>

          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid #dbe4ec", backgroundColor: "#fff", minWidth: 0 }}>
            <Typography variant="overline" sx={{ color: "#355070", fontWeight: 700 }}>
              Graph Workspace
            </Typography>
            <Typography variant="h6" sx={{ mt: 1 }}>
              {activePatternLabel}
            </Typography>
            <Typography sx={{ color: "#607086", mb: 2 }}>
              Hover a node to preview its residue in the structure viewer. Click a node to lock the highlight and switch the viewer focus when needed.
            </Typography>

            <Box
              ref={graphContainerRef}
              sx={{
                minHeight: 400,
                borderRadius: 3,
                border: "1px solid #d5dfeb",
                backgroundColor: "#f8fbff",
                overflow: "hidden",
              position: "relative"
              }}
            >
              {graphEmptyByChainFilter ? (
                <Box
                  sx={{
                    minHeight: 400,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    px: 4
                  }}
                >
                  <Box>
                    <Typography variant="h6">No nodes remain after chain filtering</Typography>
                    <Typography sx={{ mt: 1, color: "#607086" }}>
                      Re-enable one or more chains in the control rail to bring the synchronized graph back into view.
                    </Typography>
                  </Box>
                </Box>
              ) : graphEmptyWithoutData ? (
                <Box
                  sx={{
                    minHeight: 400,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    px: 4
                  }}
                >
                  <Box>
                    <Typography variant="h6">No graph data available for this selection</Typography>
                    <Typography sx={{ mt: 1, color: "#607086" }}>
                      This pattern does not currently expose any nodes or links in the normalized explorer view.
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <MemoForceGraph2D
                  ref={graphRef}
                  graphData={filteredGraphData}
                  width={graphSize.width}
                  height={graphSize.height}
                  nodeLabel={node => node.label}
                  nodeCanvasObject={drawNode}
                  nodePointerAreaPaint={handleNodePointerAreaPaint}
                  onNodeHover={handleNodeHover}
                  onNodeClick={handleNodeClick}
                  linkCanvasObjectMode={() => "replace"}
                  linkCanvasObject={drawLink}
                  linkDirectionalParticles={link => (link.edgeKind === "mapping" && !isLargeGraph ? 2 : 0)}
                  linkDirectionalParticleWidth={1.8}
                  warmupTicks={0}
                  cooldownTicks={isLargeGraph ? 45 : 80}
                  d3AlphaDecay={isLargeGraph ? 0.14 : 0.08}
                  d3VelocityDecay={isLargeGraph ? 0.32 : 0.24}
                  enableNodeDrag={false}
                  onEngineStop={handleGraphEngineStop}
                />
              )}
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}
